/** Находки движка правил и итоговый скоринг. */

export const SEVERITIES = ['critical', 'high', 'medium', 'low', 'info'] as const;
export type Severity = (typeof SEVERITIES)[number];

export const CATEGORIES = [
  'spoofing',
  'automation',
  'inconsistency',
  'hardware',
  'network',
  'privacy',
  'identity',
] as const;
export type Category = (typeof CATEGORIES)[number];

export const FLAGS = [
  'PROXY_VPN',
  'HOSTING',
  'TOR',
  'ANTIDETECT',
  'AUTOMATION',
  'SPOOFING',
  'INCONSISTENT',
  'BOT',
  'VIRTUAL_MACHINE',
  'PRIVACY_TOOLING',
] as const;
export type Flag = (typeof FLAGS)[number];

/** Пара «что с чем не сошлось» — основа объяснения для пользователя. */
export interface Evidence {
  label: string;
  expected?: unknown;
  actual?: unknown;
  /** Свободные детали, когда пара expected/actual не подходит. */
  detail?: string;
}

/** Насколько находка сама по себе позволяет приписать окружению подмену. */
export const EVIDENCE_TIERS = [
  'direct',
  'corroborating',
  'contextual',
  'observation',
] as const;
export type EvidenceTier = (typeof EVIDENCE_TIERS)[number];

/**
 * Откуда взялась находка.
 *
 * `network` — из самого HTTP-запроса, подделать из JavaScript нельзя.
 * `browser-verified` — сервер пересчитал вывод по сырым измерениям заново.
 * `browser-attested` — измерено браузером, проверить сервер не может.
 *
 * Поле заполняет только сервер. У находок, посчитанных в браузере локально,
 * его нет — и это честно: доверять им как серверным нельзя.
 */
export type FindingLayer = 'network' | 'browser-verified' | 'browser-attested';

export interface Finding {
  ruleId: string;
  title: string;
  layer?: FindingLayer;
  severity: Severity;
  category: Category;
  flags: Flag[];
  /** Вклад в снижение скора после применения confidence. */
  weight: number;
  /** 0..1 — насколько правило уверено в этом конкретном срабатывании. */
  confidence: number;
  /** Объяснение для пользователя, обычным языком. */
  explanation: string;
  evidence: Evidence[];
  /** Прямое доказательство либо улика, требующая независимого подтверждения. */
  evidenceTier?: EvidenceTier;
  /** Семейство источника; находки одной группы не подтверждают друг друга. */
  evidenceGroup?: string;
  /**
   * Находка теневого правила: считается и логируется, но в вердикт не идёт.
   * В публичный отчёт сервер такие находки не кладёт вовсе — поле нужно для
   * клиентского предпросмотра и для симметрии со схемой сервера.
   */
  shadow?: boolean;
}

export interface CategoryScore {
  category: Category;
  score: number;
  findings: number;
}

export interface ScoreResult {
  /** 0..100, где 100 — окружение выглядит полностью доверенным. */
  trustScore: number;
  /** clean | suspicious | high_risk | network_risk | genuine_browser_with_vpn | fraudulent */
  verdict:
    | 'clean'
    | 'suspicious'
    | 'high_risk'
    | 'network_risk'
    | 'genuine_browser_with_vpn'
    | 'fraudulent';
  /**
   * Подлинность браузера: тот ли он, за кого себя выдаёт. Считается по
   * всему, кроме сети. Пользование VPN — это риск, а не подделка, и в одно
   * число с подменой браузера оно не складывается.
   */
  authenticity: number;
  /** Доверие к соединению: туннель, дата-центр, утечки. */
  networkTrust: number;
  /**
   * Покрытие сетевого слоя: IP-репутация, TLS/TCP-отпечатки и внешние
   * UDP/QUIC/DNS-пробы. Низкое значение означает не «чисто», а «часть
   * сетевых проверок не удалось выполнить».
   */
  networkCoverage: number;
  flags: Flag[];
  byCategory: CategoryScore[];
  bySeverity: Record<Severity, number>;
  /** Доля слоёв, которые удалось проверить — низкая уверенность ≠ чистота. */
  coverage: number;
  /**
   * Что ограничило оценку сверху, если ограничивало.
   *
   * Отличать «нашли расхождения» от «не смогли проверить» обязательно: числа
   * в этих двух случаях могут совпасть, а означают они противоположное.
   * Без этого поля пользователь с исправным браузером и недоступным сетевым
   * слоем читал бы «есть подозрительные признаки» — и был бы прав, возмутившись.
   */
  limitedBy?: 'coverage' | 'network-layer';
  /**
   * Насколько можно доверять самому вердикту, а не насколько он суров. 0..1.
   *
   * `trustScore` отвечает «насколько подозрительно», это поле — «насколько мы
   * вообще уверены». Обвинение тем надёжнее, чем прямее вызвавшая его улика;
   * «чисто» — чем полнее была проверка. Публичному чекеру поле обязательно:
   * незнакомец читает вердикт о себе, и «не уверены» не должно выглядеть как
   * приговор.
   */
  confidence: number;
}

/** Описание правила, независимое от реализации. */
export interface RuleMeta {
  id: string;
  title: string;
  severity: Severity;
  category: Category;
  flags: Flag[];
  weight: number;
  evidenceTier?: EvidenceTier;
  evidenceGroup?: string;
  /** Какие модули сигналов нужны правилу для работы. */
  requires: string[];
  /**
   * Этап правила. Пусто или 'active' — участвует в вердикте. 'shadow' —
   * считается и логируется, но в счёт не идёт: обкатка на живом трафике,
   * пока правило не доказано на размеченных прогонах.
   */
  stage?: 'active' | 'shadow';
}
